/**
 * /incidents — public incident ledger.
 *
 * Reader-facing surface for the /api/incidents/public endpoint.
 * Renders each incident as a self-contained lesson:
 *   headline · narrative · chain receipt · transferable pattern
 *
 * Dual-purpose by design: humans read the narrative, agents fetch
 * the same JSON via the API. Both audiences see the same content
 * with the same receipts.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import {
  ArrowLeft,
  AlertTriangle,
  ShieldCheck,
  Hash,
  Clock,
  ScrollText,
  Link2,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function Incidents() {
  const [state, setState] = useState({ status: "loading", data: null, err: null });

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await fetch(`${API}/api/incidents/public`);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        if (alive) setState({ status: "ok", data, err: null });
      } catch (e) {
        if (alive) setState({ status: "error", data: null, err: e.message });
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  return (
    <div
      data-testid="incidents-page"
      className="min-h-screen bg-[#08080a] text-zinc-200 font-sans"
    >
      <div className="max-w-3xl mx-auto px-5 sm:px-8 py-10">
        <Link
          to="/"
          data-testid="incidents-back-home"
          className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-zinc-500 hover:text-emerald-300 mb-8"
        >
          <ArrowLeft className="h-3 w-3" /> back to sentinel
        </Link>

        <header className="mb-10">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.4em] text-amber-300 mb-3">
            <ScrollText className="h-3 w-3" /> Incident Ledger · Public
          </div>
          <h1 className="text-4xl sm:text-5xl font-heading tracking-[-0.02em] leading-[1.05] mb-5">
            What we hit, how we caught it, what you can learn.
          </h1>
          <p className="text-zinc-400 text-base sm:text-[17px] leading-relaxed">
            Every entry below is a real event that was sealed to the
            Sentinel&rsquo;s local Never-Harm chain. Nothing is hypothetical.
            Each entry is written for two readers: a builder who wants to
            copy the mechanism, and a successor AI agent that will read
            the same content programmatically at{" "}
            <code className="text-emerald-300 text-[0.9em]">
              GET /api/incidents/public
            </code>
            .
          </p>
        </header>

        {state.status === "loading" && (
          <div
            data-testid="incidents-loading"
            className="text-zinc-500 text-sm py-16"
          >
            Loading the ledger&hellip;
          </div>
        )}

        {state.status === "error" && (
          <div
            data-testid="incidents-error"
            className="border border-red-500/40 bg-red-500/[0.03] p-5 text-sm text-red-300"
          >
            Could not load the incident ledger: {state.err}
          </div>
        )}

        {state.status === "ok" && (
          <>
            <div
              data-testid="incidents-ledger-meta"
              className="border border-zinc-800 bg-[#0b0b0d] p-4 mb-10 text-[11px] font-mono text-zinc-500 flex flex-wrap gap-x-6 gap-y-1"
            >
              <span>
                <span className="text-zinc-400">entries:</span>{" "}
                <span className="text-emerald-300">
                  {state.data.entry_count}
                </span>
              </span>
              <span>
                <span className="text-zinc-400">ledger sha256:</span>{" "}
                <span className="text-emerald-300">
                  {(state.data.ledger_sha256 || "").slice(0, 12)}&hellip;
                </span>
              </span>
              <span>
                <span className="text-zinc-400">chain scope:</span>{" "}
                local backend
              </span>
            </div>

            {state.data.entries.map((e) => (
              <IncidentCard key={e.entry_number} entry={e} />
            ))}

            <div className="mt-16 border-t border-zinc-800 pt-8 text-[11px] font-mono text-zinc-500 leading-relaxed">
              <div className="text-zinc-400 mb-2 uppercase tracking-[0.3em] text-[10px]">
                For agents
              </div>
              <div className="mb-2">
                {state.data.for_agents?.how_to_use}
              </div>
              <div className="text-zinc-600">
                Canonical reading order:{" "}
                {(state.data.for_agents?.canonical_reading_order || []).join(
                  " → "
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function IncidentCard({ entry }) {
  const ce = entry.chain_event || {};
  return (
    <article
      data-testid={`incident-entry-${entry.entry_number}`}
      className="border border-zinc-800 bg-[#0b0b0d] p-6 sm:p-7 mb-8"
    >
      <div className="flex items-center gap-3 text-[10px] uppercase tracking-[0.4em] text-amber-300 mb-3">
        <AlertTriangle className="h-3 w-3" />
        <span>
          Entry #{String(entry.entry_number).padStart(3, "0")}
        </span>
        {entry.severity && (
          <span className="text-zinc-500 normal-case tracking-normal">
            · {entry.severity}
          </span>
        )}
      </div>

      <h2 className="text-2xl sm:text-3xl font-heading tracking-[-0.01em] mb-4">
        {entry.title}
      </h2>

      <div className="flex flex-wrap gap-4 text-[11px] font-mono text-zinc-500 mb-6">
        {entry.date && (
          <span className="inline-flex items-center gap-1.5">
            <Clock className="h-3 w-3" /> {entry.date}
          </span>
        )}
        {entry.category && (
          <span className="text-zinc-400">{entry.category}</span>
        )}
      </div>

      <div className="prose prose-invert prose-sm max-w-none prose-p:text-zinc-300 prose-p:leading-relaxed prose-headings:text-emerald-300 prose-headings:tracking-normal prose-headings:font-heading prose-headings:text-base prose-headings:uppercase prose-headings:tracking-[0.15em] prose-code:text-amber-300 prose-code:bg-zinc-900 prose-code:px-1 prose-code:rounded prose-code:before:hidden prose-code:after:hidden prose-strong:text-zinc-100">
        <ReactMarkdown
          // Strip the "Chain seal:" line — we render it as a receipt card below
          components={{
            p: ({ node, children, ...props }) => {
              const text = String(children);
              if (text.startsWith("**Chain seal:**") || text.startsWith("**Date:**") || text.startsWith("**Category:**") || text.startsWith("**Severity:**")) {
                return null;
              }
              return <p {...props}>{children}</p>;
            },
          }}
        >
          {entry.body_markdown}
        </ReactMarkdown>
      </div>

      {/* Chain receipt — the seal that backs the narrative */}
      {ce.event_hash && (
        <div
          data-testid={`incident-${entry.entry_number}-receipt`}
          className="mt-6 border border-emerald-500/30 bg-emerald-500/[0.02] p-4"
        >
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-emerald-300 mb-3">
            <ShieldCheck className="h-3 w-3" /> Chain Receipt
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-2 gap-x-6 text-[11px] font-mono text-zinc-400">
            <div>
              <span className="text-zinc-500">gate:</span>{" "}
              <span className="text-amber-300">{ce.gate}</span>
            </div>
            <div>
              <span className="text-zinc-500">outcome:</span>{" "}
              <span className="text-emerald-300">{ce.outcome}</span>
            </div>
            <div className="sm:col-span-2 break-all">
              <span className="text-zinc-500">event_hash:</span>{" "}
              <span className="text-emerald-200">{ce.event_hash}</span>
            </div>
            <div className="sm:col-span-2 break-all">
              <span className="text-zinc-500">prev_hash:</span>{" "}
              <span className="text-zinc-300">{ce.prev_hash}</span>
            </div>
            <div className="sm:col-span-2">
              <span className="text-zinc-500">timestamp:</span>{" "}
              <span className="text-zinc-300">{ce.timestamp}</span>
            </div>
          </div>
        </div>
      )}

      {ce.event_hash && (
        <div className="mt-3 text-[11px] text-zinc-500 flex items-center gap-2">
          <Link2 className="h-3 w-3" />
          <span>
            Verify at{" "}
            <code className="text-emerald-300">/api/pgrl/never-harm/verify</code>
            {" "}by walking from this event_hash.
          </span>
        </div>
      )}

      {/* Cross-chain fallback — when the receipt lives on the origin chain
          (e.g. Preview) and this deployment is a different chain (e.g.
          Production), the entry's chain_event is null. The narrative is
          still authoritative; the cryptographic seal simply lives on the
          chain where the event was sealed. Two-chain topology by design. */}
      {!ce.event_hash && (
        <div
          data-testid={`incident-${entry.entry_number}-cross-chain-note`}
          className="mt-6 border border-zinc-800 bg-zinc-950/60 p-4"
        >
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-zinc-400 mb-2">
            <Link2 className="h-3 w-3" /> Chain Scope Note
          </div>
          <p className="text-[11px] font-mono text-zinc-500 leading-relaxed">
            {entry.seal_event_id ? (
              <>
                This entry was sealed on the origin chain as event{" "}
                <code className="text-amber-300/80 break-all">
                  {entry.seal_event_id}
                </code>
                {" "}under gate{" "}
                <code className="text-amber-300/80">{entry.seal_gate || "—"}</code>.
                {" "}The full cryptographic receipt is available on the chain
                where the event was sealed. This deployment runs an
                independent Never-Harm chain by architectural design; see the
                {" "}<code className="text-emerald-300">chain_scope</code>{" "}
                field on{" "}
                <code className="text-emerald-300">/api/incidents/public</code>.
              </>
            ) : (
              <>
                Chain event sealed on origin chain. This deployment runs an
                independent Never-Harm chain by architectural design — the
                narrative above documents the pattern; the cryptographic
                receipt lives on the chain where the event occurred.
              </>
            )}
          </p>
        </div>
      )}
    </article>
  );
}
